/**
 * Person name display helpers.
 * Prefer these whenever showing a member's first/last/full name in the UI.
 */

/**
 * Append "(You)" to a display name when it belongs to the current user.
 * Matches existing UI copy in tasks / schedule collaborators.
 */
export const withYouLabel = name => {
  const base = String(name || "").trim() || "You"
  return base.includes("(You)") ? base : `${base} (You)`
}

export const isSameUserId = (a, b) => {
  if (a == null || b == null) return false
  return String(a) === String(b)
}

/**
 * Capitalize each word in a person name (first, last, hyphenated, apostrophes).
 * Leaves emails and empty values unchanged.
 */
export const formatDisplayName = value => {
  const text = String(value ?? "").trim()
  if (!text) return ""
  if (text.includes("@")) return text

  const base = text.replace(/\s*\(You\)\s*$/i, "").trim()
  if (!base) return ""

  return base
    .split(/([\s'-]+)/)
    .map(part => {
      if (!part || /^[\s'-]+$/.test(part)) return part
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    })
    .join("")
}

/**
 * Build a capitalized full name from a user-like object or raw string.
 */
export const getUserFullName = (user, { fallback = "" } = {}) => {
  if (user == null) return fallback
  if (typeof user === "string") {
    return formatDisplayName(user) || fallback
  }

  const raw =
    user.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
    user.name ||
    ""

  const formatted = formatDisplayName(raw)
  if (formatted) return formatted
  if (user.email) return String(user.email).trim()
  return fallback
}

export const nameWithYou = (name, isYou) => {
  const formatted = formatDisplayName(name)
  if (isYou) return withYouLabel(formatted || "You")
  return formatted || name || "—"
}
